import { Navigate, Route, Routes, useLocation } from "react-router-dom";

import { useAuth } from "./auth/AuthContext";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { Landing } from "./pages/Landing";
import { NotFound } from "./pages/NotFound";
import { Login } from "./pages/Login";
import { Register } from "./pages/Register";
import { RegisterStudyId } from "./pages/RegisterStudyId";
import { ForgotPassword } from "./pages/ForgotPassword";
import { ResetPassword } from "./pages/ResetPassword";
import { VerifyEmail } from "./pages/VerifyEmail";
import { Dashboard } from "./pages/Dashboard";
import { Collab } from "./pages/Collab";
import { Admin } from "./pages/Admin";
import { SsoDone } from "./pages/SsoDone";
import { SkeletonRows } from "./components/Skeleton";
import type { ReactElement } from "react";

// Session-restore placeholder: a page-shaped shimmer, not a spinner/text.
function RouteLoading(): ReactElement {
  return (
    <div className="full-center route-loading">
      <SkeletonRows rows={4} />
    </div>
  );
}

function RequireAuth({ children }: { children: ReactElement }): ReactElement {
  const { status } = useAuth();
  if (status === "loading") {
    return <RouteLoading />;
  }
  if (status === "anon") {
    return <Navigate to="/login" replace />;
  }
  return children;
}

// Collaborator-only area. Authenticated non-collaborators fall back to their
// participant dashboard.
function RequireCollaborator({ children }: { children: ReactElement }): ReactElement {
  const { status, user } = useAuth();
  if (status === "loading") {
    return <RouteLoading />;
  }
  if (status === "anon") {
    return <Navigate to="/login" replace />;
  }
  if (user?.role !== "collaborator") {
    return <Navigate to="/app" replace />;
  }
  return children;
}

// Admin-only area. Authenticated non-admins fall back to their dashboard.
function RequireAdmin({ children }: { children: ReactElement }): ReactElement {
  const { status, user } = useAuth();
  if (status === "loading") {
    return <RouteLoading />;
  }
  if (status === "anon") {
    return <Navigate to="/login" replace />;
  }
  if (user?.role !== "admin") {
    return <Navigate to="/app" replace />;
  }
  return children;
}

export function App(): ReactElement {
  // Keyed on the pathname so a crashed page's boundary resets on navigation
  // (a class boundary otherwise keeps showing its fallback after Back/links).
  const location = useLocation();
  return (
    <ErrorBoundary key={location.pathname}>
      <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/register/study-id" element={<RegisterStudyId />} />
      <Route path="/join/:slug" element={<RegisterStudyId />} />
      {/* Short print URL for the NeuroAge study materials. */}
      <Route path="/neuroage" element={<Navigate to="/join/neuroage" replace />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/sso/done" element={<SsoDone />} />
      <Route
        path="/app"
        element={
          <RequireAuth>
            <Dashboard />
          </RequireAuth>
        }
      />
      <Route
        path="/collab"
        element={
          <RequireCollaborator>
            <Collab />
          </RequireCollaborator>
        }
      />
      <Route
        path="/admin"
        element={
          <RequireAdmin>
            <Admin />
          </RequireAdmin>
        }
      />
      <Route path="*" element={<NotFound />} />
      </Routes>
    </ErrorBoundary>
  );
}
